import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TablePagination from '@mui/material/TablePagination';
import type { TablePaginationOwnProps } from '@mui/material/TablePagination/TablePagination';
import { ChevronLeft, ChevronRight, PageFirst, PageLast } from '@opengov/react-capital-assets';
import React from 'react';
import { ITEMS_PER_PAGE_OPTION } from '../../../constants/publications';
import { styles } from './StoryListTablePagination.styles';

type Props = {
  totalRows: number;
  page: number; // 0-based pagination
  rowsPerPage: number;
  itemName: string;
  dataTest: string;
  onPageChange: TablePaginationOwnProps['onPageChange'];
  onRowsPerPageChange: TablePaginationOwnProps['onRowsPerPageChange'];
};

export function StoryListTablePagination({
  totalRows,
  page,
  rowsPerPage,
  itemName,
  dataTest,
  onPageChange,
  onRowsPerPageChange
}: Props) {
  return (
    <TablePagination
      component="div"
      count={totalRows}
      page={page}
      onPageChange={onPageChange}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={onRowsPerPageChange}
      rowsPerPageOptions={ITEMS_PER_PAGE_OPTION}
      labelRowsPerPage={`${itemName} per page:`}
      labelDisplayedRows={({ from, to, count }) => (
        <Box component="span" sx={styles.paginationLabel}>
          <strong>{from}</strong> to <strong>{to}</strong> of <strong>{count}</strong>
        </Box>
      )}
      data-test={dataTest}
      ActionsComponent={CustomPaginationActions}
      sx={styles.tablePagination}
    />
  );
}

export function CustomPaginationActions({
  count,
  page,
  rowsPerPage,
  onPageChange
}: {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: TablePaginationOwnProps['onPageChange'];
}) {
  return (
    <Box sx={styles.paginationActions}>
      <IconButton onClick={(event) => onPageChange(event, 0)} disabled={page === 0} size="small">
        <PageFirst fontSize="small" />
      </IconButton>
      <IconButton onClick={(event) => onPageChange(event, page - 1)} disabled={page === 0} size="small">
        <ChevronLeft fontSize="small" />
      </IconButton>
      <Box component="span" sx={styles.paginationLabel}>
        Page <strong>{page + 1}</strong> of <strong>{Math.ceil(count / rowsPerPage)}</strong>
      </Box>
      <IconButton
        onClick={(event) => onPageChange(event, page + 1)}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        size="small"
      >
        <ChevronRight fontSize="small" />
      </IconButton>
      <IconButton
        onClick={(event) => onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        size="small"
      >
        <PageLast fontSize="small" />
      </IconButton>
    </Box>
  );
}
